/**
 * PDF downloads - g.pdf(doc, params, opts)
 *
 * The client side of the PDF document system (server: /frm/pdf/render;
 * the whole mechanism: genus-php/docs/pdf.md). One call generates a
 * DECLARED document and handles BOTH answer shapes:
 *
 *   - the PDF itself → triggers the browser download (opts.download:
 *     false skips it and hands you the blob instead);
 *   - the busy conversion → { queued: true, task } — the server moved
 *     the job to a background task. With opts.wait the call itself
 *     polls /frm/pdf/task and, once done, downloads the finished file
 *     through the private-files delivery (filesUrl + file.path, the
 *     session cookie) exactly like the instant road — the busy case
 *     costs the page nothing (bsp's ask, Sep 14 2026). Without opts.wait
 *     the page polls itself: `done` answers fileId (lid) + `file`
 *     (name, path… — the display shape) to link or download.
 *
 * Always returns an envelope-shaped object:
 *   { success: true,  data: { downloaded: true, filename, blob, task?, fileId?, file? } }
 *   { success: true,  data: { queued: true, task, timedOut? } }
 *   { success: true,  error: { code, ... } }        (business refusal)
 *   { success: false, error: { code, message } }    (transport failure;
 *                                                     6 = the background
 *                                                     task FAILED)
 * so callers branch exactly like after g.fetch.
 *
 * opts.wait: true | <ms> — after a queued answer, poll until done (the
 *   number = the patience, default 120000 ms; past it the queued shape
 *   comes back with timedOut: true and the page may keep polling).
 * opts.background: true — DEV environments only: force the queued road
 *   (the server ignores it on beta/production) to exercise the busy UI.
 * opts.download: false — no automatic download, data.blob instead.
 * opts.progress: false / opts.signal — as in g.fetch.
 */

import { resolveEndpoint, standardHeaders, apiFetch as gfetch } from './fetch.js';
import { translate } from './translate.js';
import { progressStart, progressEnd } from './progress.js';
import { settings } from './core.js';

const failure = (code, message) => ({ success: false, error: { code, message } });
const POLL_MS = 1500;
const DEFAULT_WAIT_MS = 120000;
const sleep = (ms, signal) => new Promise((resolve) => {
	const t = setTimeout(resolve, ms);
	if (signal) signal.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
});

// the browser download trigger — one road for the instant bytes and the
// background file fetched through the delivery gate
function triggerDownload(blob, filename) {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * The wait road: poll /frm/pdf/task until done/failed or the patience
 * runs out; a done task downloads through the private-files delivery.
 */
async function waitForTask(task, doc, opts) {
	const patience = typeof opts.wait === 'number' && opts.wait >= 0 ? opts.wait : DEFAULT_WAIT_MS;
	const until = Date.now() + patience;
	let first = true;
	while (true) {
		if (opts.signal?.aborted) return failure(4, translate('Request aborted'));
		if (!first) {
			const left = until - Date.now();
			if (left <= 0) return { success: true, error: { code: 0, message: '' }, data: { queued: true, task, timedOut: true } };
			await sleep(Math.min(POLL_MS, left), opts.signal);
		}
		first = false;
		const polled = await gfetch('/frm/pdf/task', { task }, { progress: false, signal: opts.signal, unguarded: true });
		if (!polled.success || polled.error.code !== 0) return polled; // 1061 (expired / not yours) or a transport failure, verbatim
		const state = polled.data || {};
		if (state.status === 'failed') return failure(6, state.error || translate('The document could not be generated.'));
		if (state.status !== 'done') continue;
		const file = state.file || null;
		const filename = file?.name || `${doc}.pdf`;
		if (!file?.path) return failure(3, translate('Invalid API response'));
		// the delivery gate: the creator's session cookie is the identity
		const base = String(settings.filesUrl || '').replace(/\/+$/, '');
		const src = /^https?:\/\//.test(file.path) ? file.path : `${base}/${file.path}`;
		let res;
		try {
			res = await window.fetch(src, { credentials: 'include', signal: opts.signal });
		} catch (err) {
			if (err && err.name === 'AbortError') return failure(4, translate('Request aborted'));
			return failure(2, translate('API not responding'));
		}
		if (!res.ok) return failure(2, translate('API not responding'));
		const blob = await res.blob();
		if (opts.download !== false) triggerDownload(blob, filename);
		return { success: true, error: { code: 0, message: '' }, data: { downloaded: opts.download !== false, filename, blob, task, fileId: state.fileId ?? null, file } };
	}
}

export async function pdf(doc, params, opts = {}) {
	const target = resolveEndpoint('/frm/pdf/render');
	if (!target) return failure(1, translate('Invalid endpoint: {endpoint}', { endpoint: '/frm/pdf/render' }));
	const headers = standardHeaders(target);
	headers['Content-Type'] = 'application/json';

	if (opts.progress !== false) progressStart();
	let res;
	try {
		res = await window.fetch(target.url, {
			method: 'POST',
			credentials: 'include',
			headers,
			body: JSON.stringify({ data: { doc, params: params || {}, ...(opts.background === true ? { background: true } : {}) } }),
			signal: opts.signal,
		});

		const type = res.headers.get('content-type') || '';
		if (type.includes('application/pdf')) {
			const blob = await res.blob();
			const filename = dispositionFilename(res.headers.get('content-disposition')) || `${doc}.pdf`;
			if (opts.download !== false) triggerDownload(blob, filename);
			return { success: true, error: { code: 0, message: '' }, data: { downloaded: opts.download !== false, filename, blob } };
		}

		// everything else is the JSON envelope: queued conversion, the data
		// function's refusal/validation, or an API error — pass it through
		let envelope;
		try {
			envelope = JSON.parse(await res.text());
		} catch {
			return failure(3, translate('Invalid API response'));
		}
		const waiting = opts.wait === true || (typeof opts.wait === 'number' && opts.wait >= 0);
		if (waiting && envelope?.success === true && envelope.error?.code === 0 && envelope.data?.queued === true && typeof envelope.data.task === 'string') {
			return await waitForTask(envelope.data.task, doc, opts);
		}
		return envelope;
	} catch (err) {
		if (err && err.name === 'AbortError') return failure(4, translate('Request aborted'));
		return failure(2, translate('API not responding'));
	} finally {
		if (opts.progress !== false) progressEnd();
	}
}

// Content-Disposition: prefer the RFC 5987 utf-8 name, fall back to the
// quoted ascii one
function dispositionFilename(header) {
	if (!header) return null;
	const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8) {
		try { return decodeURIComponent(utf8[1]); } catch { /* fall through */ }
	}
	const plain = header.match(/filename="([^"]+)"/i);
	return plain ? plain[1] : null;
}
